/**
 * Platform Detection Service
 * 
 * Detects whether the app is running on iOS, Android, or Web
 * Used for hybrid payment system to show appropriate payment methods
 */

import { Capacitor } from '@capacitor/core';

export type Platform = 'ios' | 'android' | 'web';

/**
 * Check if running on iOS native platform
 */
export const isIOS = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
};

/**
 * Check if running on Android native platform
 */
export const isAndroid = (): boolean => {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
};

/**
 * Check if running on any native platform (iOS or Android)
 */
export const isNativePlatform = (): boolean => {
  return Capacitor.isNativePlatform();
};

/**
 * Check if running on web platform
 */
export const isWeb = (): boolean => {
  return !Capacitor.isNativePlatform();
};

/**
 * Get the current platform
 */
export const getPlatform = (): Platform => {
  if (!Capacitor.isNativePlatform()) {
    return 'web';
  }
  return Capacitor.getPlatform() as Platform;
};

/**
 * Get human-readable platform name
 */
export const getPlatformDisplayName = (): string => {
  const platform = getPlatform();
  switch (platform) {
    case 'ios':
      return 'iOS';
    case 'android':
      return 'Android';
    case 'web':
      return 'Web';
    default:
      return 'Unknown';
  }
};

/**
 * Get the appropriate payment method label based on platform
 */
export const getPaymentMethodLabel = (): string => {
  const platform = getPlatform();
  switch (platform) {
    case 'ios':
      return 'Apple In-App Purchase';
    case 'android':
      return 'Google Play';
    case 'web':
      return 'PayPal';
    default:
      return 'PayPal';
  }
};

/**
 * Check if In-App Purchase is available on current platform
 */
export const isIAPAvailable = (): boolean => {
  return isNativePlatform();
};
